import React, { useEffect, useState } from 'react'
import { isEqual } from 'lodash'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Select, { components } from 'react-select'

import ExtendedInput from './ExtendedInput/ExtendedInput'
import FormElement from './FormElement'

const DropdownIndicator = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <i className="icon icon--icon_chevron_down_black icon--s" />
        </components.DropdownIndicator>
    )
}

const SelectSearch = ({
    form,
    field,
    options,
    placeholder,
    loadingPlaceholder,
    emptyPlaceholder,
    loading,
    validating,
    displayErrorsOnlyWhenDirty,
    optionNameKey = null,
    optionValueKey = null,
    inlineDescription,
    size,
    quickList,
    fieldSchema,
    ...props
}) => {
    const { name, value } = field
    const { touched, errors, dirty } = form
    const { resetFieldsOnChange } = fieldSchema
    const [defaultValue, setDefaultValue] = useState(value)
    const error = (!displayErrorsOnlyWhenDirty || (displayErrorsOnlyWhenDirty && dirty)) && touched[name] && errors[name]

    const displayLoadingPlaceholder = loading && !!loadingPlaceholder
    const displayPlaceholder = !value && !displayLoadingPlaceholder && !!placeholder
    const displayEmptyPlaceholder = !value && !displayLoadingPlaceholder && !!emptyPlaceholder && (!options || options.length <= 0)

    useEffect(() => {
        setDefaultValue(value)
    }, [value])

    const renderOption = (option, key) => {
        const value = optionValueKey ? option[optionValueKey] : option.value
        const name = optionNameKey ? option[optionNameKey] : option.name
        return { key: key, label: name, value: value }
    }

    const renderOptions = (options) => {
        const list = []
        options.forEach((option, i) => {
            list.push(renderOption(option, i))
        })
        return list
    }

    const renderOptionGroup = (option) => {
        const label = option.label
        return { label: label, options: renderOptions(option.options) }
    }

    const opts = options.map((option, i) => {
        if (option.label) {
            return renderOptionGroup(option, i)
        } else {
            return renderOption(option, i)
        }
    })

    const handleChange = (value) => {
        setDefaultValue(value?.value)
        const { setFieldValue } = form
        setFieldValue(name, value?.value)
        if (resetFieldsOnChange) {
            resetFieldsOnChange.forEach(function (fieldName) {
                setFieldValue(fieldName, '') // Reset the specified fields
                fieldName === name && setDefaultValue('') // Reset the default value if the current field is being reset
            })
        }
    }

    const formatGroupLabel = (option) => <span>{option.label}</span>

    let foundArray = null
    const displayValue = () => {
        if (name === 'make') {
            for (const category of opts) {
                foundArray = category.options.find(option => option.value === value)
                if (foundArray) {
                    return foundArray
                }
            }
        } else {
            return opts.find(opt => opt.value === value)
        }
    }

    return (
        <FormElement
            elementClass="form-element--select"
            name={name}
            value={value}
            error={error}
            loading={loading}
            validating={validating}
            inlineDescription={inlineDescription}
            {...props}
        >
            {() => (
                <ExtendedInput
                    type="text"
                    size={size}
                >
                    <Select
                        {...field}
                        {...props}
                        className={classNames('react-select', {
                            'react-select__error': error
                        })}
                        classNamePrefix="react-select"
                        options={opts}
                        formatGroupLabel={formatGroupLabel}
                        placeholder={
                            (displayPlaceholder && placeholder) ||
                            (displayLoadingPlaceholder && loadingPlaceholder)
                        }
                        value={defaultValue && displayValue()}
                        onChange={(val) => handleChange(val)}
                        components={{ DropdownIndicator }}
                        noOptionsMessage={() => displayEmptyPlaceholder && emptyPlaceholder}
                    >
                    </Select>
                </ExtendedInput>
            )}
        </FormElement>
    )
}

SelectSearch.propTypes = {
    quickList: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        options: PropTypes.arrayOf(PropTypes.shape({
            value: PropTypes.string,
            name: PropTypes.string,
        })),
    })),
}

function areEqual (prevProps, nextProps) {
    if (prevProps.loading !== nextProps.loading) return false
    if (prevProps.validating !== nextProps.validating) return false
    if (prevProps.field.value !== nextProps.field.value) return false
    if (!isEqual(prevProps.meta, prevProps.meta)) return false
    if (!isEqual(prevProps.form.errors, nextProps.form.errors)) return false
    if (!isEqual(prevProps.form.submitCount, nextProps.form.submitCount)) return false
    return isEqual(prevProps.options, nextProps.options)
}

export default React.memo(SelectSearch, areEqual)
